import { defineStore } from 'pinia'
import Api from '../api/Api'
import AccesslinkCalls from '@/api/modules/AccesslinkCalls.js';

export const useAccesslinkStore = defineStore('accesslink', {
  state: () => ({
    accesslinks: [],

    // For public api calls
    accesslinkInfo: {},
    accesslinkPods: [],
    accesslinkNodes: [],

    viewPod: null,

    comments: [],
    listViewType: 'list',
    quickSwitcherVisible: false,
    viewPodArchiveContents: null,
    viewPodListArchiveContentsError: null,
    isRefreshingPodArchiveContents: false,
  }),
  getters: {
    getAccesslinks: function(state) {
      return state.accesslinks;
    },

    // for public api calls
    getAccesslinkInfo: function(state) {
      return state.accesslinkInfo;
    },
    getAccesslinkPods: function(state) {
      return state.accesslinkPods;
    },
    getAccesslinkNodes: function(state) {
      return state.accesslinkNodes;
    },
    getViewPod: function(state) {
      return state.viewPod;
    },
    getComments: function(state) {
      return state.comments;
    },
    getListViewType: function(state) {
      return state.listViewType;
    },
    isQuickSwitcherVisible: function(state) {
      return state.quickSwitcherVisible;
    },
    quickSwitcherPods: function(state) {
      return (nodeId) => {
        if (nodeId) {
          const node = state.accesslinkNodes.find((node) => node.id === nodeId);
          console.assert(node, `Could not find node with id ${nodeId}`);
          return node.pods;
        }

        return state.accesslinkPods;
      };
    },
    archivePodContents: function (state) {
      return state.viewPodArchiveContents;
    },
    listArchivePodContentsError: function (state) {
      return state.viewPodListArchiveContentsError;
    },
    isRefreshingArchivePodContents: function(state) {
      return state.isRefreshingPodArchiveContents;
    }
  },
  actions: {
    toggleListViewType: function() {
      if (this.listViewType === 'list') {
        this.listViewType = 'grid';
      } else {
        this.listViewType = 'list';
      }
    },
    toggleQuickSwitcher: function() {
      this.quickSwitcherVisible = !this.quickSwitcherVisible;
    },

    refreshAccesslinks: async function (nodeId) {
      const response = await Api.accesslink.getAccesslinksByNode(nodeId);
      this.accesslinks = response.data || [];
    },

    createAccesslink: async function(payload) {
      const response = await Api.accesslink.createAccesslink(payload);
      return response.data;
    },

    updateAccesslink: async function(payload) {
      const updatePayload = { ...payload };
      delete updatePayload.id;
      delete updatePayload.name;
      delete updatePayload.customidentifier;

      const response = await Api.accesslink.updateAccesslink(payload.id, updatePayload);
      return response.data;
    },

    deleteAccesslink: async function (accessLinkId) {
      const response = await Api.accesslink.deleteAccesslink(accessLinkId);

      for (let i = 0; i < this.accesslinks.length; i++) {
        if (this.accesslinks[i].id === accessLinkId) {
          this.accesslinks.splice(i, 1);
          break;
        }
      }

      return response.data;
    },

    inviteAccesslink: async function (payload) {
      const response = await Api.accesslink.inviteAccesslink(payload);
      return response.data;
    },

    // External call
    refreshAccesslinkInfo: async function(code) {
      const response = await Api.accesslink.getAccesslinkInfo(code);
      this.accesslinkInfo = response.data;
      return response.data;
    },
    // External call
    authorizeAccesslink: async function(data) {
      const response = await Api.accesslink.authorizeAccesslink(data);
      if (response.data.accesstoken) {
        this.accesslinkInfo = response.data;
      }
      return response.data;
    },

    // External call
    refreshAccesslinkContents: async function() {
      const response = await Api.accesslink.getAccesslinkContents({
            accesstoken: this.getAccesslinkInfo.accesstoken,
        });
        this.accesslinkPods = response.data.pods.sort((a, b) => a.name.localeCompare(b.name));
        this.accesslinkNodes = response.data.nodes.sort((a, b) => a.name.localeCompare(b.name));
        if (response.data.pods.length === 1) {
            this.setViewPod(response.data.pods[0]);
        } else if (response.data.nodes.length === 1 && response.data.nodes[0].pods.length === 1) {
            this.setViewPod(response.data.nodes[0].pods[0]);
        }
    },
    setViewPod: function(pod) {
      if (!pod) {
        this.isRefreshingPodArchiveContents = false;
        this.viewPodArchiveContents = null;
        this.viewPodListArchiveContentsError = null;
      }

      this.viewPod = pod;
    },
    // External call
    addComment: async function(payload) {
      // add the access token
      payload.accesstoken = this.getAccesslinkInfo.accesstoken;

      const response = await Api.accesslink.addComment(payload);
      const comment = response.data;

      if (comment.start !== null) {
        comment.start /= 1000;
      }
      if (comment.end !== null) {
        comment.end /= 1000;
      }

      this.comments.push(comment);

      return comment;
    },
    // External call
    updateComment: async function(payload) {
      // add the access token
      payload.accesstoken = this.getAccesslinkInfo.accesstoken;

      const response = await Api.accesslink.updateComment(payload);
      const comment = response.data;

      if (comment.start !== null) {
        comment.start /= 1000;
      }
      if (comment.end !== null) {
        comment.end /= 1000;
      }

      for (let i = 0; i < this.comments.length; i++) {
        if (this.comments[i].id == comment.id) {
          Object.assign(this.comments[i], comment);
          break;
        }
      }

      return comment;
    },
    // External call
    deleteComment: async function(payload) {
      // add the access token
      payload.accesstoken = this.getAccesslinkInfo.accesstoken;

      await Api.accesslink.deleteComment(payload);

      const commentId = payload.id;
      const newList = [];
      for (const comment of this.comments) {
        if (comment.id !== commentId) {
          newList.push(comment);
        }
      }

      this.comments = newList;
    },
    // External call
    downloadAccesslinkFiles: function(payload) {
      // payload = {code, 'id-token', excludeAssets,}
      // console.log("context", context);

      if (!this.getAccesslinkInfo?.accesstoken) {
        return;
      }

      payload.accesstoken = this.getAccesslinkInfo.accesstoken;

      return Api.accesslink.downloadAccesslinkFiles(payload);
    },
    refreshArchivePodContents: async function() {
      if (this.viewPod !== null) {
        this.isRefreshingPodArchiveContents = true;
        this.viewPodListArchiveContentsError = null;

        while (this.viewPodArchiveContents === null) {
          try {
            const contents = await AccesslinkCalls.getArchivePodContents({
              id: this.viewPod.id,
              accesstoken: this.getAccesslinkInfo.accesstoken,
            });

            if (!this.viewPod) {
              return;
            }

            if (contents) {
              this.viewPodArchiveContents = contents;
            } else {
              await new Promise((resolve) => {
                setTimeout(resolve, 5000);
              });
            }
          } catch (err) {
            if (err?.response?.data?.error) {
              this.viewPodListArchiveContentsError = err.response.data.error.message;
            } else {
              console.error(err);
            }

            break;
          } finally {
            this.isRefreshingPodArchiveContents = false;
          }
        }
      }
    }
  },
});
